import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const socialLinks = [
    { href: 'https://www.facebook.com/', icon: 'fa-facebook', label: 'Facebook', color: '#1877f2' },
    { href: 'https://www.instagram.com/', icon: 'fa-instagram', label: 'Instagram', color: '#e1306c' },
    { href: 'https://www.linkedin.com/', icon: 'fa-linkedin', label: 'LinkedIn', color: '#0a66c2' },
    { href: 'https://twitter.com/', icon: 'fa-twitter', label: 'Twitter', color: '#1da1f2' },
];

const footerLinks = [
    {
        title: 'Services',
        links: [
            { label: 'Professional Services', to: '/services/professional-services' },
            { label: 'Data Center Solutions', to: '/services/data-center' },
            { label: 'Cyber Security', to: '/services/cyber-security' },
            { label: 'Managed Services', to: '/services/managed-services' },
            { label: 'Collaboration Services', to: '/services/collaboration-services' },
            { label: 'Enterprise Networking', to: '/services/enterprise-networking' },
        ],
    },
    {
        title: 'Company',
        links: [
            { label: 'Home', to: '/' },
            { label: 'About Us', to: '/about' },
            { label: 'Career', to: '/career' },
            { label: 'Partners', to: '/partners' },
            { label: 'Contact Us', to: '/contact' },
        ],
    },
    {
        title: 'Resources',
        links: [
            { label: 'Case Studies', to: '/case-study' },
            { label: 'Blog & Insights', to: '/blogs' },
            { label: 'Industries', to: '/industries' },
            { label: 'Sustainability', to: '/sustainability' },
            { label: 'Diversity & Inclusion', to: '/diversity-inclusion' },
        ],
    },
    {
        title: 'Legal',
        links: [
            { label: 'Privacy Policy', to: '/privacy-policy' },
            { label: 'Terms & Conditions', to: '/terms' },
            { label: 'Cookie Policy', to: '#' },
            { label: 'Accessibility', to: '#' },
        ],
    },
];

function FooterLink({ to, children }) {
    const [hovered, setHovered] = React.useState(false);
    const isExternal = to === '#' || to.startsWith('http');
    const style = {
        textDecoration: 'none',
        color: hovered ? '#3b82f6' : '#64748b',
        fontSize: '1rem',
        transition: 'color 0.25s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    };
    if (isExternal) {
        return (
            <a href={to} style={style} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                {hovered && <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'inline-block', flexShrink: 0 }} />}
                {children}
            </a>
        );
    }
    return (
        <Link to={to} style={style} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
            {hovered && <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'inline-block', flexShrink: 0 }} />}
            {children}
        </Link>
    );
}

function SocialIcon({ href, icon, label, color }) {
    const [hovered, setHovered] = useState(false);
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                border: `1px solid ${hovered ? color : '#e2e8f0'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: hovered ? '#fff' : '#64748b',
                backgroundColor: hovered ? color : 'transparent',
                textDecoration: 'none',
                transition: 'all 0.25s ease',
                transform: hovered ? 'translateY(-3px)' : 'none',
            }}
        >
            <i className={`fa ${icon}`} style={{ fontSize: '16px' }} />
        </a>
    );
}

export default function Footer() {
    return (
        <footer style={{
            background: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <div className="container" style={{ position: 'relative', zIndex: 1, padding: '64px 16px 0' }}>

                {/* ── Top: Brand + Newsletter ── */}
                <motion.div
                    className="row gy-5 mb-5 pb-2 align-items-start"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.65 }}
                    viewport={{ once: true }}
                >
                    {/* Brand */}
                    <div className="col-lg-5">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '10px',
                                background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2" />
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#fff" strokeWidth="2" />
                                    <path d="M2 12h20" stroke="#fff" strokeWidth="2" />
                                </svg>
                            </div>
                            <span style={{
                                fontSize: '1.5rem', fontWeight: 800, color: '#0f172a',
                                letterSpacing: '-0.5px', lineHeight: 1,
                            }}>
                                Solaris
                            </span>
                        </div>
                        <p style={{ color: '#94a3b8', fontSize: '1rem', lineHeight: '1.75', maxWidth: '380px', marginBottom: '28px' }}>
                            Empowering enterprises with certified IT infrastructure, cybersecurity, managed services, and digital transformation solutions across India and beyond.
                        </p>

                        {/* Contact chips */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <a href="mailto:response@solaris.co.in" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                color: '#64748b', textDecoration: 'none', fontSize: '1rem', transition: 'color 0.2s',
                            }}
                                onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
                                onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
                            >
                                <span style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: 'rgba(59,130,246,0.12)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <i className="fa fa-envelope" style={{ color: '#60a5fa', fontSize: '13px' }} />
                                </span>
                                response@solaris.co.in
                            </a>
                            <a href="tel:+919717375675" style={{
                                display: 'inline-flex', alignItems: 'flex-start', gap: '10px',
                                color: '#64748b', textDecoration: 'none', fontSize: '1rem', transition: 'color 0.2s',
                            }}
                                onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
                                onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
                            >
                                <span style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: 'rgba(59,130,246,0.12)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px',
                                }}>
                                    <i className="fa fa-phone" style={{ color: '#60a5fa', fontSize: '13px' }} />
                                </span>
                                <span>
                                    +91 7571807772<br />
                                    +91 52249 55788
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* Spacer */}
                    <div className="col-lg-1" />

                    {/* Newsletter */}
                    <div className="col-lg-6">
                        <div style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '16px',
                            padding: '32px',
                        }}>
                            <h5 style={{ color: '#1a202c', fontWeight: 700, marginBottom: '8px', fontSize: '1.1rem' }}>
                                Stay ahead of the curve
                            </h5>
                            <p style={{ color: '#64748b', fontSize: '1rem', marginBottom: '20px' }}>
                                Get the latest insights on cybersecurity, cloud, and IT infrastructure - straight to your inbox.
                            </p>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    style={{
                                        flex: '1 1 200px',
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        background: '#ffffff',
                                        color: '#1a202c',
                                        fontSize: '1rem',
                                        outline: 'none',
                                    }}
                                />
                                <button style={{
                                    padding: '10px 22px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    transition: 'opacity 0.2s',
                                }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    Subscribe
                                </button>
                            </div>

                            {/* Social icons */}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                                {socialLinks.map(s => <SocialIcon key={s.label} {...s} />)}
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ── Divider ── */}
                <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0 30%, #e2e8f0 70%, transparent)', margin: '0 0 48px' }} />

                {/* ── Link Columns ── */}
                <motion.div
                    className="row g-4 mb-5"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    viewport={{ once: true }}
                >
                    {footerLinks.map((col) => (
                        <div key={col.title} className="col-6 col-md-3">
                            <h6 style={{
                                color: '#1a202c',
                                fontWeight: 700,
                                fontSize: '1rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                marginBottom: '20px',
                                textAlign: 'left'
                            }}>
                                {col.title}
                            </h6>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {col.links.map(link => (
                                    <li key={link.label}>
                                        <FooterLink to={link.to}>{link.label}</FooterLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </motion.div>

                {/* ── Bottom Bar ── */}
                <motion.div
                    style={{
                        borderTop: '1px solid #e2e8f0',
                        padding: '24px 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                    }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                        © {new Date().getFullYear()} Solaris . All rights reserved.
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                        Designed & Developed with{' '}
                        <span style={{ color: '#ef4444' }}>♥</span>{' '}
                        by{' '}
                        <a href="https://www.gemperts.com" target="_blank" rel="noopener noreferrer"
                            style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 500 }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                            Gemperts
                        </a>
                    </p>
                </motion.div>
            </div>
        </footer>
    );
}
